import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchGroups } from "features/management/vehicleGroup/groupsThunks";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import EditCard from "./EditCard";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  IconButton,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDPagination from "components/MDPagination";
import MDTypography from "components/MDTypography";
import { deleteGroups } from "config/apis";

const GroupTable = () => {
  const dispatch = useDispatch();
  const { groups, filterSearch, status, error } = useSelector(
    (state) => state.groups
  );

  const [openEditPopup, setOpenEditPopup] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(7);
  const [search, setSearch] = useState("");

  const handleEdit = (groupId) => {
    setSelectedGroupId(Number(groupId));
    setOpenEditPopup(true);
  };

  const handleDeleteClick = (groupId) => {
    setGroupToDelete(Number(groupId));
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(deleteGroups, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-dhundhoo-session": token,
        },
        body: JSON.stringify({ groupId: groupToDelete }),
      });

      if (!response.ok) throw new Error("Failed to delete group");

      setOpenDeleteDialog(false);
      setGroupToDelete(null);
      dispatch(fetchGroups());
    } catch (err) {
      console.error("Delete Error:", err);
      alert("Failed to delete group: " + err.message);
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setGroupToDelete(null);
  };

  useEffect(() => {
    dispatch(fetchGroups());
  }, [dispatch]);

  if (status === "loading")
    return (
      <Box sx={{ textAlign: "center", mt: 5 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Box>
    );

  if (status === "failed")
    return (
      <Alert severity="error" sx={{ mt: 2, maxWidth: "500px", mx: "auto" }}>
        {error}
      </Alert>
    );

  const filteredGroups = groups.filter((group) =>
    filterSearch || search
      ? group.groupName
          .toLowerCase()
          .includes((filterSearch || search).toLowerCase())
      : true
  );

  const paginatedGroups = filteredGroups.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const totalPages = Math.ceil(filteredGroups.length / rowsPerPage);

  return (
    <>
      <Paper
        sx={{
          mx: "auto",
          mt: 5,
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={3}
          borderBottom="1px solid #eee"
        >
          <MDTypography variant="h5" fontWeight="bold" color="text.primary">
            Group List
          </MDTypography>

          <TextField
            size="small"
            placeholder="Search groups..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: "250px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />
        </MDBox>

        <TableContainer>
          <Table>
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>
                  Group Name
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>
                  Assigned Vehicles
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>
                  Created Time
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedGroups.length > 0 ? (
                paginatedGroups.map((group) => (
                  <TableRow
                    key={group.groupId}
                    sx={{
                      "&:hover": { backgroundColor: "#fafafa" },
                      transition: "all 0.2s ease",
                    }}
                  >
                    <TableCell>{group.groupName}</TableCell>
                    <TableCell>{group.assignedVehicles}</TableCell>
                    <TableCell>
                      {new Date(group.createdTimeStamp).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          color="success"
                          size="small"
                          onClick={() => handleEdit(group.groupId)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeleteClick(group.groupId)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No groups found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={3}
          py={2}
          borderTop="1px solid #eee"
        >
          <MDTypography variant="button" color="secondary">
            Showing {page * rowsPerPage + 1}-
            {Math.min((page + 1) * rowsPerPage, filteredGroups.length)} of{" "}
            {filteredGroups.length} entries
          </MDTypography>

          {totalPages > 1 && (
            <MDPagination color="info" variant="gradient">
              {page > 0 && (
                <MDPagination item onClick={() => setPage(page - 1)}>
                  <KeyboardArrowLeftIcon />
                </MDPagination>
              )}
              {Array.from({ length: totalPages }).map((_, idx) => (
                <MDPagination
                  key={idx}
                  item
                  active={idx === page}
                  onClick={() => setPage(idx)}
                >
                  {idx + 1}
                </MDPagination>
              ))}
              {page < totalPages - 1 && (
                <MDPagination item onClick={() => setPage(page + 1)}>
                  <KeyboardArrowRightIcon />
                </MDPagination>
              )}
            </MDPagination>
          )}
        </MDBox>
        <EditCard
          open={openEditPopup}
          onClose={() => setOpenEditPopup(false)}
          groupId={selectedGroupId}
        />
      </Paper>

      <Dialog open={openDeleteDialog} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this group?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>No</Button>
          <Button color="error" onClick={handleConfirmDelete}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GroupTable;
